/**
 * Widget Réservations Propriétaire
 * Phase 3.2 - Gestion des réservations optimisée
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';

import { DashboardWidget, StatusIndicator } from '../DashboardBase';
import { 
  STATUS_COLORS, 
  PERIOD_LABELS 
} from '../../../constants/dashboardConstants';

const OwnerReservationsWidget = ({ data, loading, period, onRefresh }) => {
  if (!data && !loading) {
    return (
      <DashboardWidget
        title="Réservations"
        loading={loading}
        onRefresh={onRefresh}
      >
        <Typography color="text.secondary">
          Aucune donnée de réservations disponible
        </Typography>
      </DashboardWidget>
    );
  }

  const {
    total = 0,
    pending = 0,
    confirmed = 0,
    active = 0,
    completed = 0,
    cancelled = 0,
    by_status = {}
  } = data || {};

  // Configuration des statuts avec icônes et couleurs
  const statusConfig = {
    pending: {
      label: 'En attente',
      icon: <ScheduleIcon />,
      color: 'warning'
    },
    confirmed: {
      label: 'Confirmées',
      icon: <CheckCircleIcon />,
      color: 'info'
    },
    active: {
      label: 'Actives',
      icon: <EventIcon />,
      color: 'success'
    },
    completed: {
      label: 'Terminées',
      icon: <CheckCircleIcon />,
      color: 'primary'
    },
    cancelled: {
      label: 'Annulées',
      icon: <CancelIcon />,
      color: 'error'
    }
  };

  // Statistiques rapides
  const quickStats = [
    { label: 'Total', value: total, color: 'primary' },
    { label: 'Actives', value: active, color: 'success' },
    { label: 'En attente', value: pending, color: 'warning' },
    { label: 'Annulées', value: cancelled, color: 'error' }
  ];

  // Calcul des taux
  const activeRate = total > 0 ? Math.round((active / total) * 100) : 0;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const cancellationRate = total > 0 ? Math.round((cancelled / total) * 100) : 0;

  return (
    <DashboardWidget
      title={`Réservations - ${PERIOD_LABELS[period]}`}
      loading={loading}
      onRefresh={onRefresh}
      height={500}
    >
      {/* Statistiques rapides */}
      <Grid container spacing={2} mb={3}>
        {quickStats.map((stat, index) => (
          <Grid item xs={3} key={index}>
            <Paper 
              sx={{ 
                p: 1.5, 
                textAlign: 'center',
                bgcolor: `${stat.color}.50`,
                border: 1,
                borderColor: `${stat.color}.200`
              }}
            >
              <Typography variant="h5" color={`${stat.color}.main`} fontWeight="bold">
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Taux de performance */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Indicateurs de performance
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Taux d'activité
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  {activeRate}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={activeRate} 
                sx={{ height: 6, borderRadius: 3 }}
                color="success"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Taux de réalisation
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {completionRate}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={completionRate} 
                sx={{ height: 6, borderRadius: 3 }}
                color="primary"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Taux d'annulation
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="error.main">
                  {cancellationRate}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={cancellationRate} 
                sx={{ height: 6, borderRadius: 3 }}
                color="error"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Détail par statut */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Répartition par statut
        </Typography>
        
        <List>
          {Object.entries(by_status).map(([status, count]) => {
            const config = statusConfig[status];
            if (!config || count === 0) return null;
            
            const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
            
            return (
              <ListItem key={status} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: `${config.color}.main` }}>
                    {config.icon}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body1">
                        {config.label}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight="bold">
                          {count}
                        </Typography>
                        <Chip
                          label={`${percentage}%`}
                          size="small"
                          color={config.color}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{ 
                        mt: 1, 
                        height: 4, 
                        borderRadius: 2,
                        bgcolor: 'grey.200'
                      }}
                      color={config.color}
                    />
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Conseils */}
      {cancellationRate > 15 && (
        <Box mt={2} p={2} bgcolor="warning.50" borderRadius={2}>
          <Typography variant="caption" color="warning.dark">
            ⚠️ <strong>Attention :</strong> Votre taux d'annulation est élevé ({cancellationRate}%). 
            Considérez réviser vos conditions d'annulation ou améliorer la communication avec les locataires.
          </Typography>
        </Box>
      )}
    </DashboardWidget>
  );
};

export default OwnerReservationsWidget;
